import { useState } from 'react';
import i18n from '../i18n';

function transformPersona(persona) {
  const safeArray = (val) => Array.isArray(val) ? [...val] : [];
  const safeStr = (val) => val || '';

  return {
    name: safeStr(persona.name),
    follower_base_percentage: persona.follower_base_percentage || 0,
    follower_base_is_estimated: persona.follower_base_is_estimated || false,
    description: safeStr(persona.description),
    intro_narrative: safeStr(persona.intro_narrative),
    demographics: {
      full_name: safeStr(persona.demographics?.full_name),
      job_title: safeStr(persona.demographics?.job_title),
      age_range: safeStr(persona.demographics?.age_range),
      gender: safeStr(persona.demographics?.gender),
      lifestyle_factors: safeStr(persona.demographics?.lifestyle_factors),
      income_level: safeStr(persona.demographics?.income_level),
      education_level: safeStr(persona.demographics?.education_level)
    },
    personal_context: {
      career_trajectory: safeStr(persona.personal_context?.career_trajectory),
      daily_responsibilities: safeStr(persona.personal_context?.daily_responsibilities),
      success_metrics: safeStr(persona.personal_context?.success_metrics),
      tools_platforms_used: safeArray(persona.personal_context?.tools_platforms_used)
    },
    professional_background: {
      industry: safeStr(persona.professional_background?.industry),
      experience: safeStr(persona.professional_background?.experience),
      skills: safeArray(persona.professional_background?.skills)
    },
    goals_challenges: {
      primary_goal: safeStr(persona.goals_challenges?.primary_goal),
      secondary_objective: safeStr(persona.goals_challenges?.secondary_objective),
      key_challenges: safeStr(persona.goals_challenges?.key_challenges),
      urgency_level: safeStr(persona.goals_challenges?.urgency_level),
      current_workarounds: safeArray(persona.goals_challenges?.current_workarounds)
    },
    decision_making_process: {
      role: safeStr(persona.decision_making_process?.role),
      evaluation_criteria: safeArray(persona.decision_making_process?.evaluation_criteria),
      research_methods: safeArray(persona.decision_making_process?.research_methods),
      key_objections: safeStr(persona.decision_making_process?.key_objections),
      other_stakeholders: safeStr(persona.decision_making_process?.other_stakeholders)
    },
    communication_preferences: {
      preferred_channels: safeArray(persona.communication_preferences?.preferred_channels),
      content_types: safeArray(persona.communication_preferences?.content_types),
      language_style: safeStr(persona.communication_preferences?.language_style),
      events_communities: safeArray(persona.communication_preferences?.events_communities)
    },
    value_proposition: {
      how_it_helps: safeStr(persona.value_proposition?.how_it_helps),
      key_benefits: safeArray(persona.value_proposition?.key_benefits),
      valuable_features: safeArray(persona.value_proposition?.valuable_features),
      messaging_angle: safeStr(persona.value_proposition?.messaging_angle)
    },
    engagement_strategy: {
      best_channels: safeArray(persona.engagement_strategy?.best_channels),
      content_topics: safeArray(persona.engagement_strategy?.content_topics),
      call_to_action: safeStr(persona.engagement_strategy?.call_to_action),
      objections_to_prepare_for: safeStr(persona.engagement_strategy?.objections_to_prepare_for)
    }
  };
}

export default function usePersonaGenerator(t) {
  const [account, setAccount] = useState('');
  const [numPersonas, setNumPersonas] = useState(1);
  const [platform, setPlatform] = useState('twitter');
  const [surveyFile, setSurveyFile] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    setPersonas([]);

    try {
      let response;

      if (platform === 'survey') {
        if (!surveyFile) throw new Error(t('survey_file_required'));
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(surveyFile);
        });

        response = await fetch('/api/generate-personas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            numPersonas: parseInt(numPersonas, 10),
            platform: 'survey',
            language: i18n.language,
            account: account.trim() || surveyFile.name,
            surveyFileBase64: base64,
            surveyFileName: surveyFile.name
          })
        });
      } else {
        response = await fetch('/api/generate-personas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ account: account.trim(), numPersonas: parseInt(numPersonas, 10), platform, language: i18n.language })
        });
      }

      if (!response.ok) {
        let errorMessage = `Error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (_) { /* ignore parse error */ }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data.personas || data.personas.length === 0) {
        throw new Error('No personas were generated. Please try a different account name.');
      }

      const transformed = data.personas.map(transformPersona);
      setResult({ personas: transformed, using_default_data: data.using_default_data || false, data_source_type: data.data_source_type || 'ai-estimated', notes: data.notes || '' });
      setPersonas(transformed);
    } catch (err) {
      setError(err.message || t('error_message'));
    } finally {
      setLoading(false);
    }
  };

  return { account, setAccount, numPersonas, setNumPersonas, platform, setPlatform, surveyFile, setSurveyFile, personas, loading, error, result, handleSubmit };
}
